import { useEffect, useRef, useState } from 'react';

import { motion } from 'framer-motion';

interface ExitTypingTextProps {
  fullText: string;
  isExiting: boolean;
  speed?: number;
  onExitComplete?: () => void;
}

/**
 * ExitTypingText - A component that displays text and can animate it out
 * character by character from right to left (reverse typing effect).
 *
 * This mirrors the TypingText animation:
 * - TypingText: characters appear one by one, each fading from 0 to 1 over 2 seconds
 * - ExitTypingText: characters exit one by one, each fading from 1 to 0 over 2 seconds
 */
const ExitTypingText: React.FC<ExitTypingTextProps> = ({
  fullText,
  isExiting,
  speed = 30,
  onExitComplete
}) => {
  // Track which character index we're up to in the exit animation
  // Characters at index >= exitingFromIndex are exiting (fading out)
  const [exitingFromIndex, setExitingFromIndex] = useState<number | null>(null);
  const hasStartedExitRef = useRef(false);
  const onExitCompleteRef = useRef(onExitComplete);

  // Keep onExitComplete ref up to date without triggering re-renders
  useEffect(() => {
    onExitCompleteRef.current = onExitComplete;
  }, [onExitComplete]);

  // Reset when fullText changes
  useEffect(() => {
    setExitingFromIndex(null);
    hasStartedExitRef.current = false;
  }, [fullText]);

  // Handle exit animation
  useEffect(() => {
    if (!isExiting || hasStartedExitRef.current) return;

    hasStartedExitRef.current = true;
    let currentExitIndex = fullText.length - 1;

    const exitNextChar = () => {
      if (currentExitIndex < 0) {
        // All characters have started their exit animation
        // Wait for the fade duration before calling complete
        setTimeout(() => {
          onExitCompleteRef.current?.();
        }, 2000); // Match the 2 second transition duration

        return;
      }

      setExitingFromIndex(currentExitIndex);
      currentExitIndex--;
      setTimeout(exitNextChar, speed);
    };

    // Start the exit animation
    exitNextChar();
  }, [isExiting, fullText, speed]);

  return (
    <motion.span
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      style={{
        position: 'relative',
        display: 'inline'
      }}
    >
      <span style={{ display: 'inline' }}>
        {fullText.split('').map((char, index) => {
          // Character is exiting if exitingFromIndex is set and this index is >= exitingFromIndex
          const isCharExiting =
            exitingFromIndex !== null && index >= exitingFromIndex;

          return (
            <motion.span
              key={`${index}-${char}`}
              initial={{ opacity: 1 }}
              animate={{ opacity: isCharExiting ? 0 : 1 }}
              transition={{ duration: 2, ease: 'easeOut' }}
              style={{ display: 'inline' }}
            >
              {char}
            </motion.span>
          );
        })}
      </span>
    </motion.span>
  );
};

export default ExitTypingText;
